import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './styles.css';

export default function About() {
  // Structured data for the organization
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Raffcodes",
    "description": "Raffcodes provides reliable, innovative IT solutions and full-stack development services from Nairobi, Kenya.",
    "url": "https://raffcodes.tech/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "Raffcodes",
      "description": "IT solutions provider specializing in web development, system setup, and technical support.",
      "foundingDate": "2024",
      "founders": [
        {
          "@type": "Person",
          "name": "Raffcodes Team"
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Nairobi",
        "addressCountry": "Kenya"
      },
      "sameAs": [
        "https://twitter.com/raffcodes",
        "https://linkedin.com/company/raffcodes",
        "https://github.com/raffcodes"
      ]
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>About Raffcodes | IT Solutions & Full-Stack Development</title>
        <meta name="description" content="Learn about Raffcodes - your trusted IT solutions provider in Nairobi. We specialize in web development, system setup, troubleshooting, and innovative tech solutions." />
        <meta name="keywords" content="about raffcodes, IT solutions Nairobi, full-stack development Kenya, tech support, web development company, Raffcodes team" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://raffcodes.tech/about" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="About Raffcodes | IT Solutions & Full-Stack Development" />
        <meta property="og:description" content="Learn about Raffcodes - your trusted IT solutions provider in Nairobi. We specialize in web development, system setup, troubleshooting, and innovative tech solutions." />
        <meta property="og:url" content="https://raffcodes.tech/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://raffcodes.tech/images/about-story.jpg" />
        <meta property="og:image:alt" content="Raffcodes Team - IT Solutions Provider" />
        <meta property="og:site_name" content="Raffcodes" />
        <meta property="og:locale" content="en_KE" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Raffcodes | IT Solutions & Full-Stack Development" />
        <meta name="twitter:description" content="Learn about Raffcodes - your trusted IT solutions provider in Nairobi. We specialize in web development, system setup, troubleshooting, and innovative tech solutions." />
        <meta name="twitter:image" content="https://raffcodes.tech/images/about-story.jpg" />
        <meta name="twitter:site" content="@raffcodes" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="page about">
        {/* Hero Section */}
        <section className="about-hero">
          <div className="about-hero-content">
            <h1>About Raffcodes</h1>
            <p className="tagline">From Idea to Interface - Transforming Technology into Solutions</p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="mission-vision">
          <div className="container">
            <div className="mission-grid">
              <div className="mission-card">
                <div className="mission-icon">
                  <i className="fas fa-bullseye"></i>
                </div>
                <h3>Our Mission</h3>
                <p>
                  To provide reliable, innovative IT solutions that empower businesses and individuals 
                  to achieve their goals through cutting-edge technology and exceptional service.
                </p>
              </div>
              <div className="mission-card">
                <div className="mission-icon">
                  <i className="fas fa-eye"></i>
                </div>
                <h3>Our Vision</h3>
                <p>
                  To be the leading IT solutions provider, recognized for transforming complex 
                  technological challenges into seamless, user-friendly experiences.
                </p>
              </div>
              <div className="mission-card">
                <div className="mission-icon">
                  <i className="fas fa-handshake"></i>
                </div>
                <h3>Our Values</h3>
                <p>
                  Integrity, innovation, reliability, and customer-centric approach drive everything we do. 
                  We believe in building lasting relationships through exceptional service.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="our-story">
          <div className="container">
            <div className="story-content">
              <div className="story-text">
                <h2>Our Story</h2>
                <p>
                  Raffcodes was founded with a simple yet powerful vision: to bridge the gap between 
                  complex technology and everyday users. What started as a passion for coding and 
                  problem-solving has evolved into a comprehensive IT solutions provider.
                </p>
                <p>
                  Our journey began with helping friends and family with computer issues, and has grown 
                  to serving businesses and individuals across Nairobi and beyond. We specialize in 
                  everything from basic system setup to advanced full-stack development.
                </p>
                <p>
                  Today, we continue to uphold our commitment to quality, reliability, and innovation. 
                  Every project we undertake is handled with the same dedication and attention to detail 
                  that defined our very first support call.
                </p>
              </div>
              <div className="story-image">
                <img 
                  src="/images/about-story.jpg" 
                  alt="Raffcodes Team - IT Solutions Provider in Nairobi" 
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="why-choose-us">
          <div className="container">
            <h2>Why Choose Raffcodes?</h2>
            <div className="features-grid">
              <div className="feature">
                <i className="fas fa-clock"></i>
                <h4>Quick Response</h4>
                <p>We understand time is valuable and respond to all inquiries within hours</p>
              </div>
              <div className="feature">
                <i className="fas fa-shield-alt"></i>
                <h4>Reliable Support</h4>
                <p>Consistent, dependable service you can count on for all your IT needs</p>
              </div>
              <div className="feature">
                <i className="fas fa-lightbulb"></i>
                <h4>Innovative Solutions</h4>
                <p>Creative approaches to solve your unique technology challenges</p>
              </div>
              <div className="feature">
                <i className="fas fa-hand-holding-usd"></i>
                <h4>Competitive Pricing</h4>
                <p>High-quality services at fair, transparent prices</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}